import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  X, KeyRound, Car, Gift, Bug, ShieldAlert, Lightbulb, Loader2, PhoneCall, Inbox, MessageSquareText,
} from "lucide-react";
import { toast } from "sonner";
import { createSupportTicket, getSupportTickets, getMyTripHistory } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import EmergencyContact from "@/components/EmergencyContact";

// Centro de ayuda (botón «?» del inicio). Va en un portal al final del
// <body>: dentro del inicio, el fondo difuminado (backdrop-filter) hace de
// caja para lo «fixed» y en el móvil la ventana quedaba recortada.
//
// Motivos en lenguaje llano; cada uno con su pregunta guía. En «Un viaje» se
// elige el viaje del historial; «Seguridad en un viaje» enseña primero el 112
// y el contacto de emergencia de la persona, y lo que se cuenta llega a
// Colygon como urgente. «Mis consultas»: las respuestas se leen aquí (antes
// solo por email, y quien entra con PIN no tiene email).
const MAX_MESSAGE = 4000;
export const SUPPORT_CATEGORIES = [
  { id: "ACCOUNT", icon: KeyRound, tone: "text-sky-800 bg-sky-50 border-sky-200" },
  { id: "TRIP", icon: Car, tone: "text-colygon-darkest bg-colygon-light/20 border-colygon-light" },
  { id: "REWARDS", icon: Gift, tone: "text-amber-800 bg-amber-50 border-amber-200" },
  { id: "BUG", icon: Bug, tone: "text-slate-800 bg-slate-50 border-slate-200" },
  { id: "SAFETY", icon: ShieldAlert, tone: "text-rose-800 bg-rose-50 border-rose-200" },
  { id: "IDEA", icon: Lightbulb, tone: "text-violet-800 bg-violet-50 border-violet-200" },
];
const LOCALES = { es: "es-ES", ca: "ca-ES", en: "en-GB", fr: "fr-FR" };

// En el iPhone, con el teclado abierto la parte visible de la pantalla
// encoge, pero lo «fixed» se sigue midiendo con la pantalla entera y la
// ventana quedaba medio tapada. Aquí se centra en lo que de verdad se ve
// (visualViewport), que además sirve donde no existe la unidad dvh.
function useVisibleArea(active) {
  const [area, setArea] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!active || !vv) return undefined;
    const update = () => setArea({ top: vv.offsetTop, height: vv.height });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [active]);
  return area;
}

function MyRequests({ t, lang }) {
  const [items, setItems] = useState(undefined);
  useEffect(() => {
    getSupportTickets().then((d) => setItems(Array.isArray(d) ? d : [])).catch(() => setItems(null));
  }, []);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(LOCALES[lang] || "es-ES", { dateStyle: "short", timeStyle: "short" }) : "");

  if (items === undefined) return <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-colygon" /></div>;
  if (items === null) return <p className="py-6 text-center text-sm text-rose-700">{t("support.mine_error")}</p>;
  if (items.length === 0) {
    return (
      <div className="py-8 text-center" data-testid="support-mine-empty">
        <Inbox className="h-9 w-9 mx-auto text-slate-300" aria-hidden="true" />
        <p className="mt-2 text-sm text-slate-600">{t("support.mine_empty")}</p>
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid="support-mine">
      {items.map((it) => (
        <li key={it.ticket_id} className="rounded-2xl border border-slate-200 p-3" data-testid="support-mine-item">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-bold text-slate-800">{t(`support.cat_${(it.category || "").toLowerCase()}`, it.category)}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${it.status === "answered" ? "bg-colygon-light/25 text-colygon-darkest" : "bg-slate-100 text-slate-700"}`}>
              {t(it.status === "answered" ? "support.status_answered" : "support.status_open")}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500 tabular-nums">{fmt(it.created_at)} · {it.ticket_id}</p>
          {it.message && <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words">{it.message}</p>}
          {it.reply && (
            <div className="mt-2 rounded-xl bg-colygon-light/15 border border-colygon-light/60 p-3 text-sm text-colygon-darkest">
              <p className="text-xs font-bold uppercase tracking-wide">{t("support.reply_from")}</p>
              <p className="mt-1 whitespace-pre-wrap break-words">{it.reply}</p>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function SupportCenter({ isOpen, onClose, initialView = "new" }) {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const [view, setView] = useState(initialView);
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState("");
  const [routeId, setRouteId] = useState("");
  const [trips, setTrips] = useState(null);
  const [sending, setSending] = useState(false);
  const area = useVisibleArea(isOpen);

  useEffect(() => { if (isOpen) setView(initialView); }, [isOpen, initialView]);

  // Los viajes del historial, solo cuando hacen falta
  const needsTrip = category === "TRIP" || category === "SAFETY";
  useEffect(() => {
    if (!needsTrip || trips) return;
    getMyTripHistory("all").then((d) => setTrips((d?.trips || []).slice(0, 15))).catch(() => setTrips([]));
  }, [needsTrip, trips]);

  const reset = () => {
    setCategory(null);
    setMessage("");
    setRouteId("");
  };

  const close = () => {
    if (sending) return;
    onClose();
    reset();
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error(t("support.err_empty"));
      return;
    }
    setSending(true);
    try {
      const res = await createSupportTicket({ category, message: message.trim(), page: pathname, route_id: routeId || null });
      toast.success(t("support.success", { id: res.ticket_id }));
      reset();
      setView("mine");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("support.err_create"));
    } finally {
      setSending(false);
    }
  };

  const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(LOCALES[lang] || "es-ES", { weekday: "short", day: "numeric", month: "short" });
  const cat = SUPPORT_CATEGORIES.find((c) => c.id === category);
  const showForm = view === "new" && category;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-slate-900/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          data-testid="support-center"
          style={area ? { top: area.top + area.height / 2, maxHeight: area.height - 32 } : undefined}
          className="fixed z-[201] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col focus:outline-none"
        >
          <div className="bg-colygon-darkest px-5 pt-4 pb-3 text-white shrink-0">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-xl font-bold">{t("support.title")}</DialogPrimitive.Title>
                <p className="text-white/80 text-sm">{t("support.subtitle")}</p>
              </div>
              <DialogPrimitive.Close className="p-2 -mr-2 hover:bg-white/10 rounded-full transition-colors shrink-0"
                aria-label={t("support.close")} data-testid="support-close">
                <X size={20} aria-hidden="true" />
              </DialogPrimitive.Close>
            </div>
            <div role="tablist" className="mt-3 grid grid-cols-2 gap-1 rounded-xl bg-white/10 p-1">
              {[["new", MessageSquareText], ["mine", Inbox]].map(([v, Icon]) => (
                <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)}
                  data-testid={`support-view-${v}`}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-sm font-semibold transition-colors ${
                    view === v ? "bg-white text-colygon-darkest" : "text-white/85 hover:bg-white/10"}`}>
                  <Icon className="h-4 w-4" aria-hidden="true" /> {t(`support.view_${v}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5 overflow-y-auto overscroll-contain">
            {view === "mine" && <MyRequests t={t} lang={lang} />}

            {view === "new" && !category && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{t("support.question")}</h3>
                <p className="mt-1 mb-4 text-sm text-slate-600">{t("support.where_reply")}</p>
                <div className="grid grid-cols-2 gap-2.5">
                  {SUPPORT_CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    return (
                      <button key={c.id} type="button" onClick={() => setCategory(c.id)} data-testid={`support-cat-${c.id}`}
                        className={`flex flex-col items-center justify-center gap-1.5 p-3 min-h-[5.75rem] rounded-2xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md ${c.tone}`}>
                        <Icon size={26} className="shrink-0" aria-hidden="true" />
                        <span className="font-bold text-sm text-center leading-tight">{t(`support.cat_${c.id.toLowerCase()}`)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showForm && (
              <div className="flex flex-col">
                <button type="button" onClick={reset} className="text-sm font-semibold text-colygon-dark mb-3 self-start hover:underline">
                  &larr; {t("support.back")}
                </button>
                <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800 mb-3">
                  <cat.icon className="h-5 w-5" aria-hidden="true" /> {t(`support.cat_${category.toLowerCase()}`)}
                </h3>

                {category === "SAFETY" && (
                  <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 space-y-3" data-testid="support-safety">
                    <p className="text-sm font-semibold text-rose-900">{t("support.safety_first")}</p>
                    <a href="tel:112" data-testid="support-call-112"
                      className="flex items-center justify-center gap-2 h-12 rounded-xl bg-rose-600 text-white text-base font-extrabold hover:bg-rose-700">
                      <PhoneCall className="h-5 w-5" aria-hidden="true" /> {t("support.call_112")}
                    </a>
                    <div>
                      <p className="text-sm text-rose-900 mb-2">{t("support.safety_contact")}</p>
                      <EmergencyContact compact />
                    </div>
                    <p className="text-sm text-rose-900">{t("support.safety_tell")}</p>
                  </div>
                )}

                {needsTrip && (
                  <label className="block mb-3 text-sm font-semibold text-slate-700">
                    {t("support.trip_pick")}
                    <select value={routeId} onChange={(e) => setRouteId(e.target.value)} data-testid="support-trip"
                      className="mt-1 w-full h-11 rounded-xl border-2 border-slate-200 bg-white px-3 text-sm font-normal focus:border-colygon outline-none">
                      <option value="">{trips === null ? t("support.trip_loading") : t("support.trip_none")}</option>
                      {(trips || []).map((tr) => (
                        <option key={tr.id} value={tr.id}>
                          {dayLabel(tr.date)} {tr.time} · {tr.origin} → {tr.destination}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label htmlFor="support-message" className="font-semibold text-slate-700 block mb-1">{t(`support.ask_${category.toLowerCase()}`)}</label>
                <p className="text-xs text-slate-500 mb-2">{t(`support.hint_${category.toLowerCase()}`)}</p>
                <textarea
                  id="support-message"
                  value={message}
                  maxLength={MAX_MESSAGE}
                  rows={5}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="support-message"
                  className="w-full min-h-[8rem] border-2 border-slate-200 rounded-xl p-3 text-base focus:border-colygon focus:ring-4 focus:ring-colygon-light/50 outline-none transition-all resize-y"
                  placeholder={t("support.placeholder")}
                />
              </div>
            )}
          </div>

          {showForm && (
            <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between gap-3 shrink-0">
              <p className="text-xs text-slate-500">{t("support.where_reply_short")}</p>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                data-testid="support-send"
                className={`inline-flex shrink-0 items-center gap-2 text-white font-bold py-3 px-5 rounded-xl shadow-md transition-all active:scale-95 disabled:opacity-50 ${
                  category === "SAFETY" ? "bg-rose-700 hover:bg-rose-800" : "bg-colygon-dark hover:bg-colygon-darkest"}`}
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {sending ? t("support.sending") : t("support.send_btn")}
              </button>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
